// Declarative recipe catalog for the "Define a cost source" gallery (Step 1).
// Each entry's `facet` / `labelKey` are recipe-local descriptors (NOT saved-criteria fields);
// `recipeToInitial` translates them into the scope-funnel shape { scope, refinements }, with
// an optional value-search seed and 0–2 guided param steps. Pure data + a small adapter — no
// React, no network.

export const RECIPES = [
  { id: "whole_scope", section: "Direct", icon: "🗂", label: "Whole project / AI workspace", mode: "whole_scope" },
  { id: "own_project", section: "Direct", icon: "📁", label: "Own a project", facet: "project" },

  { id: "by_team",    section: "Group by a tag", icon: "👥", label: "By team",        facet: "label", labelKey: "team" },
  { id: "by_env",     section: "Group by a tag", icon: "🌍", label: "By environment", facet: "label", labelKey: "env" },
  { id: "by_product", section: "Group by a tag", icon: "📦", label: "By product",     facet: "label", labelKey: "product" },
  { id: "by_tag",     section: "Group by a tag", icon: "🏷️", label: "By any tag",     facet: "label",
    paramSteps: [{ type: "pick-label-key", prompt: "Which label key do you want to group by?" }] },

  { id: "carve_shared", section: "Shared & carve-outs", icon: "✂️", label: "Carve a shared project by team",
    facet: "label", labelKey: "team",
    paramSteps: [{ type: "pick-one-to-narrow", facet: "project", narrowKey: "project", prompt: "Which shared project do you want to split?" }] },
  { id: "shared_pool",  section: "Shared & carve-outs", icon: "🪣", label: "A shared cost pool", facet: "project" },

  { id: "bigquery", section: "By service / SKU", icon: "🗄️", label: "BigQuery / analytics", facet: "sku_category", presetValueFilter: "bigquery" },
  { id: "egress",   section: "By service / SKU", icon: "🌐", label: "Network egress",      facet: "sku_category", presetValueFilter: "egress" },
  { id: "by_service", section: "By service / SKU", icon: "⚙️", label: "A specific service", facet: "service" },

  { id: "ai_workspace", section: "AI", icon: "🤖", label: "AI by workspace", facet: "ai_workspace" },

  { id: "scratch", section: "Custom", icon: "🧩", label: "Start from scratch", mode: "scratch" },
  { id: "cel",     section: "Custom", icon: "🛠️", label: "Advanced (CEL)",    mode: "cel" },
];

export const RECIPE_SECTIONS = ["Direct", "Group by a tag", "Shared & carve-outs", "By service / SKU", "AI", "Custom"];

// recipeToInitial maps a (non-custom) recipe to the picker's starting state. `scopeAxis`
// tells the builder which scope axis the recipe targets: "project" (GCP) or "ai_workspace"
// (OpenAI/AI). The two axes are mutually exclusive, so AI recipes carry no project scope and
// no refinements (label/SKU/service apply only to project-keyed GCP cost).
export function recipeToInitial(recipe) {
  const scopeAxis = recipe.facet === "ai_workspace" ? "ai_workspace" : "project";
  const scope = scopeAxis === "ai_workspace" ? { projects: [], ai_workspaces: [] } : { projects: [] };
  const refinements = [];
  if (recipe.facet === "label") {
    refinements.push({ dim: "label", key: recipe.labelKey || "", values: [] });
  } else if (recipe.facet === "sku_category" || recipe.facet === "service") {
    refinements.push({ dim: recipe.facet, values: [] });
  }
  // recipe.presetNarrow entries are the old definitional eq narrow-by filters. They become
  // additional label refinements (one per entry) ANDed onto whatever the primary recipe set up.
  for (const n of (recipe.presetNarrow || [])) {
    if (n.key && n.value) {
      refinements.push({ dim: "label", key: n.key, values: [n.value] });
    }
  }
  return {
    criteria: { scope, refinements },
    presetSearch: recipe.presetValueFilter || "",
    paramSteps: recipe.paramSteps || [],
    scopeAxis,
  };
}
